import React from "react";
import { Accordion } from "react-bootstrap";
import { DisplayBox } from "../../components";
import { ApiHelper, ArrayHelper, MessageInterface, PlaylistInterface, MessageHelper, PendingCommand } from "../../helpers";


interface Props {
  playlist: PlaylistInterface,
  sendCommand: (cmd: PendingCommand) => void
}


export const AvailableContent: React.FC<Props> = props => {

  const [yourMessages, setYourMessages] = React.useState<MessageInterface[]>(null);
  const [sharedMessages, setSharedMessages] = React.useState<MessageInterface[]>(null);
  const [feedMessages, setFeedMessages] = React.useState<MessageInterface[]>(null);
  const [playlists, setPlaylists] = React.useState<PlaylistInterface[]>(null);
  let originShape = '';

  const init = () => {
    if (originShape !== props.playlist.shape) {
      originShape = props.playlist.shape
      ApiHelper.get<PlaylistInterface[]>("/playlists/available")
        .then((data) => setPlaylists(filterByShape(data || [], props.playlist?.shape)))
        .catch(err => console.error("Failed to load available playlists:", err));
      ApiHelper.get<MessageInterface[]>("/messages")
        .then((data) => setYourMessages(filterByShape(data || [], props.playlist?.shape)))
        .catch(err => console.error("Failed to load messages:", err));
      ApiHelper.get<MessageInterface[]>("/messages/crossDepartment")
        .then((data) => setSharedMessages(filterByShape(data || [], props.playlist?.shape)))
        .catch(err => console.error("Failed to load shared messages:", err));
      ApiHelper.get<MessageInterface[]>("/messages/feedLibraries")
        .then((data) => setFeedMessages(filterByShape(data || [], props.playlist?.shape)))
        .catch(err => console.error("Failed to load feed messages:", err));
    }
  }

  const filterByShape = <T extends { shape?: string }>(items: T[], shape: string): T[] => {
    const result: T[] = [];
    items.forEach(item => { if (item.shape === shape) result.push(item) });
    return result;
  }

  React.useEffect(init, [props.playlist.shape]);

  const getPlaylistRows = () => {
    const result: React.ReactElement[] = [];
    playlists.forEach(p => {
      const playlist = p;
      if (p.id !== props.playlist?.id) result.push(<div onClick={(e) => { e.preventDefault(); addPlaylist(playlist); }}>{p.name}</div>);
    });
    return (<div className="availablePlaylists">{result}</div>);
  }

  const addMessage = (message: MessageInterface) => {
    props.sendCommand({ action: "addMessage", parameter: JSON.stringify(message) });
  }

  const addPlaylist = (playlist: PlaylistInterface) => {
    props.sendCommand({ action: "addPlaylist", parameter: JSON.stringify(playlist) });
  }

  const getMessagesRows = (messages: MessageInterface[]) => {
    const result: React.ReactElement[] = [];
    messages.forEach(m => {
      const message = m;
      result.push(<div key={"m" + m.id} onClick={(e) => { e.preventDefault(); addMessage(message); }}>
        <img src={MessageHelper.getThumbnailPath(m)} alt="thumbnail" />
        {m.name}
        <span>Duration: {m.seconds} seconds</span>
      </div>);
    });
    return (<div className="availableMessages">{result}</div>);
  }

  const getMessageAccordion = (name: string, messages: MessageInterface[]) => {
    const sections: React.ReactElement[] = [];
    messages.forEach(m => { if (!m.category) m.category = "Uncategorized" });
    const cats = ArrayHelper.getStrings(messages, "category");
    cats.forEach(c => {
      const catMessages = ArrayHelper.getAll(messages, "category", c);
      const catContent = getMessagesRows(catMessages);
      sections.push(getSection(c, c, catContent));
    });
    return (<Accordion id={name} >{sections}</Accordion>)
  }


  const getSection = (eventKey: string, title: string, contents: React.ReactElement) => {
    return (<Accordion.Item eventKey={eventKey}>
      <Accordion.Header>{title}</Accordion.Header>
      <Accordion.Body>{contents}</Accordion.Body>
    </Accordion.Item>)
  }

  const getYourMessages = () => {
    if (yourMessages?.length > 0) {
      const contents = getMessageAccordion("yourMessagesAccordion", yourMessages);
      return (getSection("yourMessages", "Your Messages", contents));
    }
  }

  const getSharedMessages = () => {
    if (sharedMessages?.length > 0) {
      const contents = getMessageAccordion("sharedMessagesAccordion", sharedMessages);
      return (getSection("sharedMessages", "Shared Messages", contents));
    }
  }

  const getFeeds = () => {
    if (feedMessages?.length > 0) {
      const contents = getMessageAccordion("feeds", feedMessages);
      return (getSection("feeds", "Feed Content", contents));
    }
  }

  const getPlaylists = () => {
    if (playlists?.length > 0) {
      const contents = getPlaylistRows();
      return (getSection("playlists", "Playlists", contents));
    }

  }


  return (
    <DisplayBox headerIcon="far fa-image" headerText="Available Content" >
      <Accordion id="availableContent" defaultActiveKey="yourMessages" >
        {getYourMessages()}
        {getSharedMessages()}
        {getFeeds()}
        {getPlaylists()}
      </Accordion>
    </DisplayBox>
  );
};